/**
 * API Service
 * Real API calls to backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/v1';

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'company' | 'cxo' | 'member' | 'admin';
  companyName?: string;
  intent?: 'hire' | 'cxo' | 'learn';
  profile?: {
    companyName?: string;
    industry?: string;
    companySize?: string;
    title?: string;
    executiveFunction?: string;
    yearsOfExperience?: string;
    hourlyRateMin?: string;
    hourlyRateMax?: string;
  };
}

interface LoginData {
  email: string;
  password: string;
  role: 'company' | 'cxo' | 'member' | 'admin';
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Array<{ msg: string; param: string }>;
}

interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    avatarUrl?: string;
    linkedinVerified?: boolean;
    linkedinId?: string;
    linkedinProfileUrl?: string;
    linkedinHeadline?: string;
  };
}

// Article Types
export interface Article {
  id: string;
  title: string;
  slug: string;
  subtitle?: string;
  excerpt?: string;
  content?: string;
  coverImageUrl?: string;
  coverImageKey?: string;
  status: 'draft' | 'published' | 'archived';
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  readTimeMinutes?: number;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  author: {
    id: string;
    name: string;
    avatarUrl?: string;
    title?: string;
    bio?: string;
    specializations?: string[];
    linkedinUrl?: string;
  };
  tags: {
    id: string;
    name: string;
    slug: string;
  }[];
  comments?: Comment[];
  metaTitle?: string;
  metaDescription?: string;
  tier?: 'free' | 'premium' | 'exclusive';
  price?: number;
  subscriberOnly?: boolean;
}

export interface Comment {
  id: string;
  content: string;
  likeCount: number;
  createdAt: string;
  author: {
    id: string;
    name: string;
    avatarUrl?: string;
    title?: string;
  };
  replies?: Comment[];
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
  articleCount: number;
}

export interface CreateArticleData {
  title: string;
  subtitle?: string;
  content: string;
  coverImageUrl?: string;
  coverImageKey?: string;
  tags?: string[];
  metaTitle?: string;
  metaDescription?: string;
  status?: 'draft' | 'published';
  tier?: 'free' | 'premium' | 'exclusive';
  price?: number;
  subscriberOnly?: boolean;
}

export interface MyArticlesResponse {
  articles: Article[];
  stats: {
    total: number;
    published: number;
    drafts: number;
    totalViews: number;
    totalLikes: number;
  };
}

// CXO Types
export interface CXOProfile {
  id: string;
  userId: string;
  name: string;
  avatarUrl?: string;
  linkedinVerified: boolean;
  title?: string;
  headline?: string;
  bio?: string;
  yearsOfExperience?: number;
  specializations: string[];
  industries: string[];
  executiveFunctions: string[];
  skills: string[];
  languages: string[];
  country?: string;
  city?: string;
  timezone?: string;
  availabilityType?: string;
  minHourlyRate?: number;
  maxHourlyRate?: number;
  currency: string;
  ratingAverage: number;
  ratingCount: number;
  totalEngagements: number;
  profileCompleteness: number;
  isPublic: boolean;
  verificationStatus: string;
  stats: {
    experienceCount: number;
    educationCount: number;
    certificationCount: number;
  };
}

export interface CXOExperience {
  id: string;
  companyName: string;
  title: string;
  location?: string;
  startDate: string;
  endDate?: string;
  isCurrent: boolean;
  description?: string;
  achievements: string[];
}

export interface CXOEducation {
  id: string;
  institution: string;
  degree?: string;
  fieldOfStudy?: string;
  startYear?: number;
  endYear?: number;
  description?: string;
}

export interface CXOCertification {
  id: string;
  name: string;
  issuingOrganization?: string;
  issueDate?: string;
  expiryDate?: string;
  credentialId?: string;
  credentialUrl?: string;
}

export interface CXOProfileDetail extends CXOProfile {
  linkedinProfileUrl?: string;
  linkedinHeadline?: string;
  memberSince: string;
  videoIntroUrl?: string;
  totalEarnings: number;
  experiences: CXOExperience[];
  educations: CXOEducation[];
  certifications: CXOCertification[];
  stats: {
    experienceCount: number;
    educationCount: number;
    certificationCount: number;
    proposalCount: number;
    engagementCount: number;
  };
}

export interface UpdateCxoProfileData {
  headline?: string;
  bio?: string;
  yearsOfExperience?: number;
  minHourlyRate?: number;
  maxHourlyRate?: number;
  currency?: string;
  availabilityType?: string;
  specializations?: string[];
  executiveFunctions?: string[];
  industries?: string[];
  skills?: string[];
  languages?: string[];
  country?: string;
  city?: string;
  timezone?: string;
  isPublic?: boolean;
}

// Brief Types
export interface Brief {
  id: string;
  title: string;
  description?: string;
  executiveFunction?: string;
  engagementType?: string;
  budgetMin?: number;
  budgetMax?: number;
  currency?: string;
  startDate?: string;
  durationWeeks?: number;
  hoursPerWeek?: number;
  locationType?: string;
  preferredCountry?: string;
  requiredSkills?: string[];
  requiredIndustries?: string[];
  minYearsExperience?: number;
  status: 'draft' | 'published' | 'closed' | 'filled';
  createdAt: string;
  updatedAt: string;
  companyId: string;
  proposals?: Proposal[];
  _count?: {
    proposals: number;
  };
}

export interface Proposal {
  id: string;
  briefId: string;
  cxoId: string;
  status: 'pending' | 'accepted' | 'rejected' | 'withdrawn';
  coverLetter?: string;
  proposedRate?: number;
  proposedDuration?: number;
  createdAt: string;
  updatedAt: string;
  cxo?: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
  cxoProfile?: {
    headline?: string;
    minHourlyRate?: number;
    maxHourlyRate?: number;
    currency?: string;
    yearsOfExperience?: number;
  };
}

export interface CreateBriefData {
  title: string;
  description?: string;
  executiveFunction?: string;
  engagementType?: string;
  budgetMin?: number;
  budgetMax?: number;
  currency?: string;
  startDate?: string;
  durationWeeks?: number;
  hoursPerWeek?: number;
  locationType?: string;
  preferredCountry?: string;
  requiredSkills?: string[];
  requiredIndustries?: string[];
  minYearsExperience?: number;
}

export interface CreateProposalData {
  briefId: string;
  coverLetter?: string;
  proposedRate?: number;
  proposedDuration?: number;
}

class ApiService {
  async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
      ...options,
    };

    // Add auth token if available
    const token = this.getToken();
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }
      
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth endpoints
  async register(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    return this.fetch<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(data: LoginData): Promise<ApiResponse<AuthResponse>> {
    return this.fetch<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: AuthResponse['user'] }>> {
    return this.fetch('/users/me');
  }

  // LinkedIn OAuth
  async linkedinAuth(
    code: string,
    action: string,
    role?: string,
    userId?: string
  ): Promise<ApiResponse<AuthResponse & { linkedin?: unknown }>> {
    return this.fetch<AuthResponse & { linkedin?: unknown }>('/auth/linkedin', {
      method: 'POST',
      body: JSON.stringify({ code, action, role, userId }),
    });
  }

  // Article endpoints
  async getArticles(params?: {
    page?: number;
    limit?: number;
    tag?: string;
    search?: string;
    sortBy?: 'publishedAt' | 'popular' | 'likes';
    order?: 'asc' | 'desc';
  }): Promise<ApiResponse<{ articles: Article[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.tag) queryParams.append('tag', params.tag);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.order) queryParams.append('order', params.order);
    
    const query = queryParams.toString();
    return this.fetch(`/articles${query ? `?${query}` : ''}`);
  }

  async getFeaturedArticles(): Promise<ApiResponse<{ articles: Article[] }>> {
    return this.fetch('/articles/featured');
  }

  async getTags(): Promise<ApiResponse<{ tags: Tag[] }>> {
    return this.fetch('/articles/tags');
  }

  async getArticle(slug: string): Promise<ApiResponse<{ article: Article }>> {
    return this.fetch(`/articles/${slug}`);
  }

  async createArticle(data: CreateArticleData): Promise<ApiResponse<{ article: { id: string; title: string; slug: string; status: string; publishedAt?: string; author: string; tags: string[] } }>> {
    return this.fetch('/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateArticle(id: string, data: Partial<CreateArticleData>): Promise<ApiResponse<{ article: { id: string; title: string; slug: string; status: string; publishedAt?: string; tags: string[] } }>> {
    return this.fetch(`/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteArticle(id: string): Promise<ApiResponse<void>> {
    return this.fetch(`/articles/${id}`, {
      method: 'DELETE',
    });
  }

  async likeArticle(id: string): Promise<ApiResponse<{ liked: boolean }>> {
    return this.fetch(`/articles/${id}/like`, {
      method: 'POST',
    });
  }

  async addComment(id: string, content: string, parentId?: string): Promise<ApiResponse<{ comment: Comment }>> {
    return this.fetch(`/articles/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content, parentId }),
    });
  }

  async getMyArticle(id: string): Promise<ApiResponse<{ article: Article }>> {
    return this.fetch(`/articles/me/${id}`);
  }

  async getMyArticles(status?: string): Promise<ApiResponse<MyArticlesResponse>> {
    const query = status ? `?status=${status}` : '';
    return this.fetch(`/articles/me/my-articles${query}`);
  }

  // Upload endpoints
  async getPresignedUrl(filename: string, contentType: string, folder: string = 'articles'): Promise<ApiResponse<{ url: string; key: string }>> {
    return this.fetch('/uploads/presigned-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, folder }),
    });
  }

  // CXO Discovery endpoints
  async discoverCxos(params?: {
    page?: number;
    limit?: number;
    search?: string;
    industry?: string | string[];
    expertise?: string | string[];
    executiveFunction?: string | string[];
    minExperience?: number;
    maxExperience?: number;
    minRate?: number;
    maxRate?: number;
    country?: string;
    city?: string;
    language?: string | string[];
    availabilityType?: string;
    sortBy?: 'relevance' | 'rating' | 'experience' | 'rate-low' | 'rate-high' | 'newest';
  }): Promise<ApiResponse<{
    cxos: CXOProfile[];
    pagination: {
      page: number;
      limit: number;
      totalCount: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    filters: {
      sortBy: string;
      applied: Record<string, unknown>;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.industry) {
      const industries = Array.isArray(params.industry) ? params.industry : [params.industry];
      industries.forEach(i => queryParams.append('industry', i));
    }
    if (params?.expertise) {
      const expertises = Array.isArray(params.expertise) ? params.expertise : [params.expertise];
      expertises.forEach(e => queryParams.append('expertise', e));
    }
    if (params?.executiveFunction) {
      const functions = Array.isArray(params.executiveFunction) ? params.executiveFunction : [params.executiveFunction];
      functions.forEach(f => queryParams.append('executiveFunction', f));
    }
    if (params?.minExperience) queryParams.append('minExperience', params.minExperience.toString());
    if (params?.maxExperience) queryParams.append('maxExperience', params.maxExperience.toString());
    if (params?.minRate) queryParams.append('minRate', params.minRate.toString());
    if (params?.maxRate) queryParams.append('maxRate', params.maxRate.toString());
    if (params?.country) queryParams.append('country', params.country);
    if (params?.city) queryParams.append('city', params.city);
    if (params?.language) {
      const languages = Array.isArray(params.language) ? params.language : [params.language];
      languages.forEach(l => queryParams.append('language', l));
    }
    if (params?.availabilityType) queryParams.append('availabilityType', params.availabilityType);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    
    const query = queryParams.toString();
    return this.fetch(`/cxos/discover${query ? `?${query}` : ''}`);
  }

  async getCxoFilters(): Promise<ApiResponse<{
    filters: {
      industries: string[];
      specializations: string[];
      executiveFunctions: string[];
      skills: string[];
      languages: string[];
      countries: string[];
      availabilityTypes: string[];
      experienceRanges: { label: string; min: number; max: number | null }[];
      rateRanges: { label: string; min: number; max: number | null }[];
    };
  }>> {
    return this.fetch('/cxos/filters');
  }

  async getCxoProfile(id: string): Promise<ApiResponse<{ cxo: CXOProfileDetail }>> {
    return this.fetch(`/cxos/${id}`);
  }

  async getMyCxoProfile(): Promise<ApiResponse<{ cxo: CXOProfile }>> {
    return this.fetch('/cxos/me/profile');
  }

  async updateMyCxoProfile(data: Partial<UpdateCxoProfileData>): Promise<ApiResponse<{ cxo: CXOProfile }>> {
    return this.fetch('/cxos/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // CXO Experience Management
  async getMyExperiences(): Promise<ApiResponse<{ experiences: CXOExperience[] }>> {
    return this.fetch('/cxos/me/experiences');
  }

  async addExperience(data: Omit<CXOExperience, 'id' | 'cxoProfileId'>): Promise<ApiResponse<{ experience: CXOExperience }>> {
    return this.fetch('/cxos/me/experiences', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExperience(id: string, data: Partial<Omit<CXOExperience, 'id' | 'cxoProfileId'>>): Promise<ApiResponse<void>> {
    return this.fetch(`/cxos/me/experiences/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteExperience(id: string): Promise<ApiResponse<void>> {
    return this.fetch(`/cxos/me/experiences/${id}`, {
      method: 'DELETE',
    });
  }

  // CXO Education Management
  async getMyEducations(): Promise<ApiResponse<{ educations: CXOEducation[] }>> {
    return this.fetch('/cxos/me/educations');
  }

  async addEducation(data: Omit<CXOEducation, 'id' | 'cxoProfileId'>): Promise<ApiResponse<{ education: CXOEducation }>> {
    return this.fetch('/cxos/me/educations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEducation(id: string, data: Partial<Omit<CXOEducation, 'id' | 'cxoProfileId'>>): Promise<ApiResponse<void>> {
    return this.fetch(`/cxos/me/educations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEducation(id: string): Promise<ApiResponse<void>> {
    return this.fetch(`/cxos/me/educations/${id}`, {
      method: 'DELETE',
    });
  }

  // CXO Certification Management
  async getMyCertifications(): Promise<ApiResponse<{ certifications: CXOCertification[] }>> {
    return this.fetch('/cxos/me/certifications');
  }

  async addCertification(data: Omit<CXOCertification, 'id' | 'cxoProfileId'>): Promise<ApiResponse<{ certification: CXOCertification }>> {
    return this.fetch('/cxos/me/certifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCertification(id: string, data: Partial<Omit<CXOCertification, 'id' | 'cxoProfileId'>>): Promise<ApiResponse<void>> {
    return this.fetch(`/cxos/me/certifications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCertification(id: string): Promise<ApiResponse<void>> {
    return this.fetch(`/cxos/me/certifications/${id}`, {
      method: 'DELETE',
    });
  }

  // Brief Management (Company)
  async createBrief(data: CreateBriefData): Promise<ApiResponse<{ brief: Brief }>> {
    return this.fetch('/briefs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyBriefs(): Promise<ApiResponse<{ briefs: Brief[] }>> {
    return this.fetch('/briefs/my-briefs');
  }

  async getBrief(id: string): Promise<ApiResponse<{ brief: Brief }>> {
    return this.fetch(`/briefs/${id}`);
  }

  async updateBrief(id: string, data: Partial<CreateBriefData>): Promise<ApiResponse<{ brief: Brief }>> {
    return this.fetch(`/briefs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async publishBrief(id: string): Promise<ApiResponse<void>> {
    return this.fetch(`/briefs/${id}/publish`, {
      method: 'POST',
    });
  }

  async deleteBrief(id: string): Promise<ApiResponse<void>> {
    return this.fetch(`/briefs/${id}`, {
      method: 'DELETE',
    });
  }

  // Brief Discovery (CXO)
  async getActiveBriefs(): Promise<ApiResponse<{ briefs: Brief[] }>> {
    return this.fetch('/briefs');
  }

  // Proposal Management
  async createProposal(data: CreateProposalData): Promise<ApiResponse<{ proposal: Proposal }>> {
    return this.fetch('/proposals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyProposals(): Promise<ApiResponse<{ proposals: Proposal[] }>> {
    return this.fetch('/proposals/my-proposals');
  }

  async getMyEngagements(): Promise<ApiResponse<{ engagements: any[] }>> {
    return this.fetch('/engagements');
  }

  async getBriefProposals(briefId: string): Promise<ApiResponse<{ proposals: Proposal[] }>> {
    return this.fetch(`/briefs/${briefId}/proposals`);
  }

  async acceptProposal(proposalId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/proposals/${proposalId}/accept`, {
      method: 'POST',
    });
  }

  async rejectProposal(proposalId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/proposals/${proposalId}/reject`, {
      method: 'POST',
    });
  }

  async withdrawProposal(proposalId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/proposals/${proposalId}/withdraw`, {
      method: 'POST',
    });
  }

  // Follow System
  async followUser(userId: string): Promise<ApiResponse<{ follow: { following: { id: string; firstName: string; lastName: string; avatarUrl?: string; cxoProfile?: { headline?: string; specializations?: string[] } } } }>> {
    return this.fetch(`/follows/${userId}/follow`, {
      method: 'POST',
    });
  }

  async unfollowUser(userId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/follows/${userId}/unfollow`, {
      method: 'DELETE',
    });
  }

  async checkFollowStatus(userId: string): Promise<ApiResponse<{ isFollowing: boolean }>> {
    return this.fetch(`/follows/${userId}/follow-status`);
  }

  async getFollowers(userId: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<{ followers: Array<{ id: string; firstName: string; lastName: string; avatarUrl?: string; role: string }>; pagination: { page: number; limit: number; total: number; totalPages: number } }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return this.fetch(`/follows/${userId}/followers${query ? `?${query}` : ''}`);
  }

  async getFollowing(userId: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<{ following: Array<{ id: string; firstName: string; lastName: string; avatarUrl?: string; role: string; cxoProfile?: { headline?: string; specializations?: string[]; industries?: string[] } }>; pagination: { page: number; limit: number; total: number; totalPages: number } }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return this.fetch(`/follows/${userId}/following${query ? `?${query}` : ''}`);
  }

  async getMyFeed(params?: { page?: number; limit?: number }): Promise<ApiResponse<{ articles: Article[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return this.fetch(`/follows/me/feed${query ? `?${query}` : ''}`);
  }

  async getFollowStats(userId: string): Promise<ApiResponse<{ followersCount: number; followingCount: number }>> {
    return this.fetch(`/follows/${userId}/follow-stats`);
  }

  async getSuggestedCxos(): Promise<ApiResponse<{ suggestions: Array<{ id: string; firstName: string; lastName: string; avatarUrl?: string; linkedinVerified: boolean; cxoProfile?: { headline?: string; specializations?: string[]; industries?: string[]; yearsOfExperience?: number; ratingAverage: number; ratingCount: number } }> }>> {
    return this.fetch('/follows/me/suggestions');
  }

  // Member API
  async getMemberProfile(): Promise<ApiResponse<{ profile: any }>> {
    return this.fetch('/members/profile');
  }

  async updateMemberProfile(data: {
    careerStage?: string;
    currentTitle?: string;
    currentCompany?: string;
    bio?: string;
    interests?: string[];
    expertise?: string[];
    preferredContentTypes?: string[];
    emailFrequency?: string;
    notifyNewFollower?: boolean;
    notifyNewArticle?: boolean;
    notifyMentions?: boolean;
  }): Promise<ApiResponse<{ profile: any }>> {
    return this.fetch('/members/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateOnboarding(data: { step: number; completed?: boolean }): Promise<ApiResponse<{ profile: any }>> {
    return this.fetch('/members/onboarding', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getMemberSuggestions(): Promise<ApiResponse<{ suggestions: any[] }>> {
    return this.fetch('/members/suggestions');
  }

  async getMemberFeed(params?: { page?: number; limit?: number }): Promise<ApiResponse<{ articles: Article[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return this.fetch(`/members/feed${query ? `?${query}` : ''}`);
  }

  async getMemberDashboard(): Promise<ApiResponse<{ stats: any }>> {
    return this.fetch('/members/dashboard');
  }

  async getTrendingCxos(limit?: number): Promise<ApiResponse<{ cxos: any[] }>> {
    const query = limit ? `?limit=${limit}` : '';
    return this.fetch(`/members/trending-cxos${query}`);
  }

  // Public CXO Profile
  async getCXOProfile(cxoId: string): Promise<ApiResponse<{ profile: CXOProfileDetail; articles: Article[] }>> {
    return this.fetch(`/members/cxos/${cxoId}`);
  }

  async followCxo(cxoId: string): Promise<ApiResponse<any>> {
    return this.fetch(`/follows/${cxoId}/follow`, {
      method: 'POST',
    });
  }

  // Subscription API (CXO Side)
  async getSubscriptionSettings(): Promise<ApiResponse<{ settings: { price: number; tier: string; subscriberCount: number } }>> {
    return this.fetch('/subscriptions/cxo/settings');
  }

  async updateSubscriptionSettings(data: { price?: number; tier?: string }): Promise<ApiResponse<{ settings: { price: number; tier: string } }>> {
    return this.fetch('/subscriptions/cxo/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getSubscribers(params?: { page?: number; limit?: number }): Promise<ApiResponse<{
    subscribers: Array<{
      id: string;
      tier: string;
      status: string;
      startedAt: string;
      expiresAt: string;
      subscriber: {
        id: string;
        name: string;
        avatarUrl?: string;
        title?: string;
        company?: string;
      }
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    }
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return this.fetch(`/subscriptions/cxo/subscribers${query ? `?${query}` : ''}`);
  }

  async getRevenue(): Promise<ApiResponse<{
    currentMonth: {
      totalRevenue: number;
      netEarnings: number;
      newSubscribers: number;
    };
    allTime: {
      totalRevenue: number;
      netEarnings: number;
      subscriptionRevenue: number;
      articleSalesRevenue: number;
    };
    activeSubscribers: number;
    recentTransactions: Array<{
      id: string;
      amount: number;
      cxoShare: number;
      date: string;
      subscriber: string;
    }>;
  }>> {
    return this.fetch('/subscriptions/cxo/revenue');
  }

  // Subscription API (Member Side)
  async subscribeToCxo(cxoId: string, tier: string = 'basic'): Promise<ApiResponse<{ subscription: any }>> {
    return this.fetch(`/subscriptions/${cxoId}/subscribe`, {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  }

  async unsubscribeFromCxo(cxoId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/subscriptions/${cxoId}/unsubscribe`, {
      method: 'DELETE',
    });
  }

  async getMySubscriptions(): Promise<ApiResponse<{ subscriptions: Array<{
    id: string;
    tier: string;
    price: number;
    status: string;
    startedAt: string;
    expiresAt: string;
    autoRenew: boolean;
    cxo: {
      id: string;
      name: string;
      avatarUrl?: string;
      title?: string;
    }
  }> }>> {
    return this.fetch('/subscriptions/my-subscriptions');
  }

  async checkSubscriptionStatus(cxoId: string): Promise<ApiResponse<{ isSubscribed: boolean; subscription: { tier: string; expiresAt: string } | null }>> {
    return this.fetch(`/subscriptions/${cxoId}/status`);
  }

  // Enhanced Comment endpoints (Phase 4.4)
  async getComments(articleId: string, params?: {
    sort?: 'newest' | 'oldest' | 'most_helpful';
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    comments: Array<{
      id: string;
      content: string;
      createdAt: string;
      updatedAt?: string;
      likeCount: number;
      helpfulCount: number;
      replyCount: number;
      author: {
        id: string;
        name: string;
        avatarUrl?: string;
        title?: string;
      };
      isLiked?: boolean;
      userReaction?: string | null;
      reactions?: Array<{ type: string; count: number }>;
      replies: Array<{
        id: string;
        content: string;
        createdAt: string;
        likeCount: number;
        author: {
          id: string;
          name: string;
          avatarUrl?: string;
          title?: string;
        };
        isLiked?: boolean;
        userReaction?: string | null;
      }>;
    }>;
    pagination: {
      page: number;
      limit: number;
      totalCount: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    const queryParams = new URLSearchParams();
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    return this.fetch(`/articles/${articleId}/comments?${queryParams.toString()}`);
  }

  async updateComment(commentId: string, content: string): Promise<ApiResponse<{ comment: Comment }>> {
    return this.fetch(`/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  async deleteComment(commentId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  async likeComment(commentId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/comments/${commentId}/like`, {
      method: 'POST',
    });
  }

  async unlikeComment(commentId: string): Promise<ApiResponse<void>> {
    return this.fetch(`/comments/${commentId}/like`, {
      method: 'DELETE',
    });
  }

  async addReaction(commentId: string, type: string): Promise<ApiResponse<void>> {
    return this.fetch(`/comments/${commentId}/react`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  }

  async removeReaction(commentId: string, type: string): Promise<ApiResponse<void>> {
    return this.fetch(`/comments/${commentId}/react`, {
      method: 'DELETE',
      body: JSON.stringify({ type }),
    });
  }

  async getCommentReactions(commentId: string): Promise<ApiResponse<{
    total: number;
    breakdown: Array<{ type: string; count: number }>;
  }>> {
    return this.fetch(`/comments/${commentId}/reactions`);
  }

  // Invoice APIs
  async getInvoices(): Promise<ApiResponse<{
    invoices: Array<{
      id: string;
      invoiceNumber: string;
      amount: number;
      subtotal: number;
      platformFee: number;
      totalAmount: number;
      currency: string;
      status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
      issueDate: string;
      dueDate: string;
      paidAt?: string;
      notes?: string;
      lineItems: Array<{ description: string; quantity: number; unitPrice: number; total: number }>;
      engagement?: { title: string };
      companyId?: string;
      cxoId?: string;
    }>;
  }>> {
    return this.fetch('/invoices');
  }

  async getInvoice(id: string): Promise<ApiResponse<{ invoice: any }>> {
    return this.fetch(`/invoices/${id}`);
  }

  async createInvoice(data: {
    engagementId: string;
    invoiceNumber: string;
    lineItems: Array<{ description: string; quantity: number; unitPrice: number }>;
    dueDate: string;
    notes?: string;
  }): Promise<ApiResponse<{ invoice: any }>> {
    return this.fetch('/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async payInvoice(id: string, transactionId: string): Promise<ApiResponse<{ invoice: any }>> {
    return this.fetch(`/invoices/${id}/pay`, {
      method: 'POST',
      body: JSON.stringify({ transactionId }),
    });
  }

  // Dashboard APIs
  async getCXODashboard(): Promise<ApiResponse<{
    cxo: {
      id: string;
      name: string;
      avatarUrl: string | null;
      linkedinVerified: boolean;
      linkedinProfileUrl: string | null;
      ratingAverage: number;
      ratingCount: number;
      profileCompleteness: number;
    };
    stats: {
      newLeads: number;
      activeProposals: number;
      activeEngagements: number;
      monthlyEarnings: number;
    };
    newLeads: Array<{
      id: string;
      company: string;
      title: string;
      budget: string;
      postedAt: string;
    }>;
    activeEngagements: Array<{
      id: string;
      company: string;
      title: string;
      progress: number;
      nextMilestone: string;
      dueDate: string;
      monthlyRate: string;
    }>;
  }>> {
    return this.fetch('/cxos/me/dashboard');
  }

  async getCompanyDashboard(): Promise<ApiResponse<{
    stats: {
      activeBriefs: number;
      shortlistedCxos: number;
      activeEngagements: number;
      unreadMessages: number;
    };
    recentBriefs: Array<{
      id: string;
      title: string;
      status: string;
      proposalsCount: number;
      createdAt: string;
    }>;
    activeEngagements: Array<{
      id: string;
      cxoName: string;
      cxoAvatar: string | null;
      title: string;
      progress: number;
      nextMilestone: string;
      dueDate: string;
      status: string;
    }>;
    recommendedCxos: Array<{
      id: string;
      name: string;
      initials: string;
      title: string;
      function: string;
      matchScore: number;
      rate: string;
    }>;
  }>> {
    return this.fetch('/briefs/company/dashboard');
  }

  async getAdminStats(): Promise<ApiResponse<{
    stats: {
      totalUsers: number;
      totalCompanies: number;
      totalCxos: number;
      totalBriefs: number;
      totalEngagements: number;
      totalRevenue: number;
      pendingVerifications: number;
      activeDisputes: number;
    };
    recentSignups: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      role: string;
      createdAt: string;
    }>;
    recentEngagements: Array<{
      id: string;
      company: { firstName: string; lastName: string; email: string };
      cxo: { firstName: string; lastName: string; email: string };
      createdAt: string;
    }>;
    marketplaceMetrics?: {
      avgMatchTimeDays: number;
      conversionRate: number;
      avgEngagementValue: number;
    };
  }>> {
    return this.fetch('/admin/stats');
  }

  // =====================================================
  // SERVICES APIs
  // =====================================================

  async getServices(): Promise<ApiResponse<{
    services: Array<{
      id: string;
      name: string;
      type: 'advisory' | 'fractional' | 'project' | 'emergency';
      description: string;
      deliverables: string[];
      hoursPerMonth: number | null;
      daysPerWeek: number | null;
      price: number;
      priceType: 'monthly' | 'hourly' | 'fixed';
      responseTime: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
  }>> {
    return this.fetch('/services/me/services');
  }

  async getService(id: string): Promise<ApiResponse<{
    service: {
      id: string;
      name: string;
      type: 'advisory' | 'fractional' | 'project' | 'emergency';
      description: string;
      deliverables: string[];
      hoursPerMonth: number | null;
      daysPerWeek: number | null;
      price: number;
      priceType: 'monthly' | 'hourly' | 'fixed';
      responseTime: string;
      isActive: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }>> {
    return this.fetch(`/services/me/services/${id}`);
  }

  async createService(data: {
    name: string;
    type: 'advisory' | 'fractional' | 'project' | 'emergency';
    description: string;
    deliverables?: string[];
    hoursPerMonth?: number;
    daysPerWeek?: number;
    price: number;
    priceType: 'monthly' | 'hourly' | 'fixed';
    responseTime: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{ service: any }>> {
    return this.fetch('/services/me/services', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateService(id: string, data: {
    name?: string;
    type?: 'advisory' | 'fractional' | 'project' | 'emergency';
    description?: string;
    deliverables?: string[];
    hoursPerMonth?: number;
    daysPerWeek?: number;
    price?: number;
    priceType?: 'monthly' | 'hourly' | 'fixed';
    responseTime?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<{ service: any }>> {
    return this.fetch(`/services/me/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteService(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.fetch(`/services/me/services/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleService(id: string): Promise<ApiResponse<{ service: any }>> {
    return this.fetch(`/services/me/services/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  async getAdminVerifications(status?: string): Promise<ApiResponse<{
    cxos: Array<{
      id: string;
      user: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        avatarUrl: string | null;
        linkedinVerified: boolean;
        createdAt: string;
      };
      title: string;
      headline: string | null;
      bio: string | null;
      specializations: string[];
      verificationStatus: string;
      linkedinUrl: string | null;
    }>;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>> {
    const query = status ? `?status=${status}` : '';
    return this.fetch(`/admin/verifications${query}`);
  }

  async updateVerificationStatus(id: string, status: string, notes?: string): Promise<ApiResponse<any>> {
    return this.fetch(`/admin/verifications/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    });
  }

  // User Profile Management
  async updateUserProfile(data: { firstName?: string; lastName?: string; phone?: string; avatarUrl?: string }): Promise<ApiResponse<{ user: { id: string; email: string; firstName: string; lastName: string; phone?: string; avatarUrl?: string; role: string } }>> {
    return this.fetch('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // CXO Profile Management
  async updateCXOProfile(data: {
    headline?: string;
    bio?: string;
    yearsOfExperience?: number;
    minHourlyRate?: number;
    maxHourlyRate?: number;
    currency?: string;
    availabilityType?: string;
    specializations?: string[];
    executiveFunctions?: string[];
    industries?: string[];
    skills?: string[];
    languages?: string[];
    country?: string;
    city?: string;
    timezone?: string;
    isPublic?: boolean;
  }): Promise<ApiResponse<{ cxo: any; message: string }>> {
    return this.fetch('/cxos/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<ApiResponse<{ message: string }>> {
    return this.fetch('/users/me/password', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Store token
  setToken(token: string): void {
    localStorage.setItem('token', token);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  removeToken(): void {
    localStorage.removeItem('token');
  }
  async post<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.fetch<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.fetch<T>(endpoint + queryString);
  }

  async patch<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.fetch<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.fetch<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

export const apiService = new ApiService();
export default apiService;
